import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Package, Plus, Archive as ArchiveIcon, ChevronDown, ChevronRight, DollarSign, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

function AddAssetDialog({ clientId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", quantity: 1, value: "", signer_name: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const val = parseFloat(form.value);
    if (!form.name.trim()) return toast.error("Description is required.");
    if (Number.isNaN(val) || val < 50) return toast.error("Asset Inventory tracks high-value items ($50.00 or higher).");
    const qty = parseInt(form.quantity, 10);
    if (!Number.isFinite(qty) || qty < 1) return toast.error("Quantity must be ≥ 1.");
    if (!form.signer_name.trim()) return toast.error("Signer name (typed signature) is required for $50+ items.");
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/property`, {
        name: form.name.trim(),
        description: form.description.trim(),
        quantity: qty,
        value: val,
        status: "active",
        signature_data: form.signer_name.trim(),
        signature_type: "provider",
        signer_name: form.signer_name.trim(),
      });
      toast.success("Asset added to inventory");
      setOpen(false);
      setForm({ name: "", description: "", quantity: 1, value: "", signer_name: "" });
      onAdded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-add-asset"><Plus className="w-4 h-4 mr-1.5" /> Add Asset Item</Button>
      </DialogTrigger>
      <DialogContent data-testid="add-asset-modal">
        <DialogHeader>
          <DialogTitle>Add Asset Item ($50+ Threshold)</DialogTitle>
          <DialogDescription>
            Personal property valued at $50.00 or higher must be logged for DSPD compliance.
            A typed signature attestation is required.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Description (item name)</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                   placeholder="e.g., Samsung Tablet" data-testid="asset-name" />
          </div>
          <div>
            <Label>Description / Serial / Notes (optional)</Label>
            <Textarea rows={2} value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Model number, serial, or distinguishing details"
                      data-testid="asset-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Quantity</Label>
              <Input type="number" min="1" value={form.quantity}
                     onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                     data-testid="asset-quantity" />
            </div>
            <div>
              <Label>Total Value (USD)</Label>
              <Input type="number" min="50" step="0.01" value={form.value}
                     onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                     placeholder="50.00" data-testid="asset-value" />
            </div>
          </div>
          <div>
            <Label>Typed Signature (attestation)</Label>
            <Input value={form.signer_name}
                   onChange={(e) => setForm((f) => ({ ...f, signer_name: e.target.value }))}
                   placeholder="Type your full name to sign" data-testid="asset-signer" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="asset-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="asset-save">{saving ? "Saving…" : "Save asset"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveConfirmDialog({ item, clientId, onArchived, onClose }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset reason state whenever the dialog opens on a different item so the
  // previous typed value never bleeds across rows.
  useEffect(() => { if (item) setReason(""); }, [item?.id]);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Reason for removal is required to archive this item.");
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/property/${item.id}/archive`, { removal_reason: reason.trim() });
      toast.success("Item archived");
      setReason("");
      onArchived?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="archive-modal">
        <DialogHeader>
          <DialogTitle>Remove / Dispose: {item?.name}</DialogTitle>
          <DialogDescription>
            This will <strong>archive</strong> the asset record (not delete it).
            A reason for removal is required and will be permanently stamped on the audit row.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Reason for Removal <span className="text-red-600">*</span></Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g., Returned to family at discharge, broken during use, lost during outing…"
            data-testid="archive-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="archive-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="archive-confirm">
            {saving ? "Archiving…" : "Archive item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PropertyInventoryPanel({ clientId }) {
  const [items, setItems] = useState(null);
  const [archiveItem, setArchiveItem] = useState(null);
  const [archivedOpen, setArchivedOpen] = useState(false);

  const load = async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}/property`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load property inventory");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, [clientId]);

  const activeItems = (items || []).filter((i) => i.status !== "archived");
  const archivedItems = (items || []).filter((i) => i.status === "archived");
  const totalValue = activeItems.reduce((acc, i) => acc + Number(i.value || 0), 0);

  return (
    <div className="space-y-4" data-testid="property-inventory">
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" />
              <p className="text-label">Active Inventory ($50+ Threshold)</p>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Total tracked value: <span className="font-bold text-foreground">{fmtMoney(totalValue)}</span>
              {" · "}{activeItems.length} active item{activeItems.length === 1 ? "" : "s"}
            </p>
          </div>
          <AddAssetDialog clientId={clientId} onAdded={load} />
        </div>

        {items === null ? (
          <p className="text-sm text-muted-foreground text-center py-4">Loading…</p>
        ) : activeItems.length === 0 ? (
          <div className="text-center py-6 border-2 border-dashed border-border rounded-md" data-testid="property-empty">
            <Package className="w-8 h-8 mx-auto text-muted-foreground mb-1" />
            <p className="text-sm font-semibold">No high-value items logged yet.</p>
            <p className="text-xs text-muted-foreground">Use &quot;Add Asset Item&quot; to record property valued at $50.00 or higher.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="property-active-list">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-secondary/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left p-2">Description</th>
                  <th className="text-right p-2 w-16">Qty</th>
                  <th className="text-right p-2 w-24">Total Value</th>
                  <th className="text-left p-2 w-44">Date Logged</th>
                  <th className="text-right p-2 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.map((i) => (
                  <tr key={i.id} className="border-b border-border hover:bg-secondary/20" data-testid={`property-row-${i.id}`}>
                    <td className="p-2 align-top">
                      <p className="font-display font-semibold">{i.name}</p>
                      {i.description && <p className="text-xs text-muted-foreground mt-0.5">{i.description}</p>}
                    </td>
                    <td className="p-2 text-right align-top tabular-nums">{i.quantity ?? 1}</td>
                    <td className="p-2 text-right align-top tabular-nums font-semibold">{fmtMoney(i.value)}</td>
                    <td className="p-2 text-xs text-muted-foreground align-top flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {fmtDate(i.created_at)}</td>
                    <td className="p-2 text-right align-top">
                      <Button
                        variant="outline" size="sm" className="text-red-700 border-red-300 hover:bg-red-50"
                        onClick={() => setArchiveItem(i)}
                        data-testid={`property-remove-${i.id}`}
                      ><ArchiveIcon className="w-3.5 h-3.5 mr-1" /> Remove / Dispose</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Archived Property Log — collapsible */}
      <Card className="p-4">
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 text-left"
          onClick={() => setArchivedOpen((v) => !v)}
          data-testid="toggle-archived-log"
        >
          <div className="flex items-center gap-2">
            <ArchiveIcon className="w-4 h-4 text-muted-foreground" />
            <p className="text-label">Archived Property Log</p>
            <Badge variant="secondary" className="text-[10px] font-bold uppercase tracking-wide">
              {archivedItems.length}
            </Badge>
          </div>
          {archivedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {archivedOpen && (
          <div className="mt-3" data-testid="property-archived-list">
            {archivedItems.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-3">No archived items.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-secondary/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left p-2">Description</th>
                      <th className="text-right p-2 w-16">Qty</th>
                      <th className="text-right p-2 w-24">Value</th>
                      <th className="text-left p-2 w-40">Archived</th>
                      <th className="text-left p-2">Reason for Removal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedItems.map((i) => (
                      <tr key={i.id} className="border-b border-border/60" data-testid={`archived-row-${i.id}`}>
                        <td className="p-2 align-top">
                          <p className="font-display font-semibold opacity-75">{i.name}</p>
                          {i.description && <p className="text-xs text-muted-foreground mt-0.5">{i.description}</p>}
                        </td>
                        <td className="p-2 text-right align-top tabular-nums opacity-75">{i.quantity ?? 1}</td>
                        <td className="p-2 text-right align-top tabular-nums opacity-75">{fmtMoney(i.value)}</td>
                        <td className="p-2 text-xs text-muted-foreground align-top">{fmtDate(i.archived_at)}<br /><span className="opacity-70">by {i.archived_by || "—"}</span></td>
                        <td className="p-2 text-xs italic align-top">{i.removal_reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      <ArchiveConfirmDialog
        item={archiveItem}
        clientId={clientId}
        onClose={() => setArchiveItem(null)}
        onArchived={load}
      />
    </div>
  );
}
